import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Sparkles, MapPin, CalendarClock, CheckCircle2, Film, Link as LinkIcon } from "lucide-react";
import { RequestPmAssign } from "@/components/RequestPmAssign";
import { EditQuotePanel } from "@/components/EditQuotePanel";

/* Client Portal M4 (2026-02) — staff review of AI-triaged New Project Requests submitted
 * directly by clients through the Client Portal. Read + "mark reviewed" only — creating the
 * real Project stays a deliberate, manual step via the existing Projects flow, this page never
 * writes to Notion.
 *
 * Edit Requests Phase 1 (2026-09): the same queue now also carries type="edit_existing" rows
 * (client already has footage, wants it edited) — same list/filter/mark-reviewed, plus PM
 * assignment and the "Edit Quote" compose/send/issue-revision panel, both staff-only in this
 * phase. */
export default function ClientRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = useState("new");

  const load = () => {
    setLoading(true);
    api.get("/v1/client-project-requests/", { params: filter ? { status: filter } : {} })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const markReviewed = async (id) => {
    try {
      await api.post(`/v1/client-project-requests/${id}/mark-reviewed`);
      toast.success("Marked as reviewed");
      load();
    } catch { toast.error("Couldn't update this request"); }
  };

  if (denied) {
    return (
      <div className="p-8" data-testid="client-requests-access-denied">
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center">
          <h1 className="font-editorial text-2xl tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-[#57534E]">Your role doesn't have permission to view Client Requests.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="client-requests-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-editorial text-4xl tracking-tight">Client Requests</h1>
          <p className="text-sm text-[#57534E] mt-1">New project requests submitted directly by clients through the Client Portal — AI-triaged, human-reviewed.</p>
        </div>
        <div className="flex gap-2">
          {["new", "reviewed", ""].map((s) => (
            <button
              key={s || "all"}
              data-testid={`client-requests-filter-${s || "all"}`}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors ${filter === s ? "bg-[#0C0C0C] text-white" : "bg-[#E7E5E4] text-[#57534E] hover:bg-[#D6D3D1]"}`}
            >{s || "All"}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#A8A29E]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center text-sm text-[#A8A29E]" data-testid="client-requests-empty">
          No {filter || ""} requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`client-request-row-${r.id}`} className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1C1C1E]">{r.company_name || "Unknown company"}</span>
                    <span className="text-xs text-[#A8A29E]">— {r.contact_name} ({r.contact_email})</span>
                    {r.type === "edit_existing" && <span className="rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#57534E]">Edit Existing Footage</span>}
                    {r.status === "reviewed" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] uppercase tracking-wider"><CheckCircle2 className="h-2.5 w-2.5" />Reviewed</span>}
                  </div>
                  {r.type === "edit_existing" ? (
                    <div className="mt-2 space-y-1.5" data-testid={`client-request-edit-intake-${r.id}`}>
                      {r.footage_links?.length > 0 && (
                        <div className="text-xs text-[#1C1C1E]">
                          <span className="text-[#57534E] uppercase tracking-wider text-[10px] mr-1"><Film className="h-2.5 w-2.5 inline mr-1" />Footage:</span>
                          {r.footage_links.map((l, i) => (
                            <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[#B45309] hover:underline mr-2"><LinkIcon className="h-2.5 w-2.5" />{l.label || l.url}</a>
                          ))}
                        </div>
                      )}
                      {r.deliverables_wanted?.length > 0 && (
                        <div className="text-xs text-[#57534E]">Wants: {r.deliverables_wanted.join(", ")}</div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-[#1C1C1E] mt-2 leading-relaxed">{r.raw_text}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    {r.ai_category_guess && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFC817]/10 border border-[#FFC817]/30 px-2 py-0.5 text-[10px] text-[#B45309]"><Sparkles className="h-2.5 w-2.5" />{r.ai_category_guess}</span>}
                    {r.ai_production_model_guess && <span className="rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] text-[#57534E]">{r.ai_production_model_guess}</span>}
                    {r.preferred_timeline && <span className="inline-flex items-center gap-1 rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] text-[#57534E]"><CalendarClock className="h-2.5 w-2.5" />{r.preferred_timeline}</span>}
                    {r.locations && <span className="inline-flex items-center gap-1 rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] text-[#57534E]"><MapPin className="h-2.5 w-2.5" />{r.locations}</span>}
                  </div>
                  {r.ai_summary && <p className="text-xs text-[#78716C] mt-2 italic">AI summary: {r.ai_summary}</p>}
                  <div className="grid sm:grid-cols-2 gap-2 mt-3">
                    <RequestPmAssign requestId={r.id} initialPm={r.assigned_pm} />
                    {r.type === "edit_existing" && <EditQuotePanel requestId={r.id} />}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 space-y-2">
                  <div className="text-[10px] font-mono text-[#A8A29E]">{r.created_at?.slice(0, 10)}</div>
                  {r.status !== "reviewed" && (
                    <button
                      data-testid={`client-request-mark-reviewed-${r.id}`}
                      onClick={() => markReviewed(r.id)}
                      className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:border-[#D6D3D1] transition-colors"
                    >Mark reviewed</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
